/**
 * スマホ（幅 768px 未満）の案件の工程表（#102、docs/39 §5.2 の9・12、§5.3.3、Figma E2・E3・E4）
 *   帯：‹ {project}／題（20px）／しるし「進み n%」「{最後の節目} 10/20」
 *   操作：週｜月・右に「見るだけ」⇔「直す」のスイッチ（既定は見るだけ）。その下に ‹ 期間 ›
 *   表：工程の列は 128px（2行まで折り返す）、行は 56px。一番上に「済み n工程」（済んだ工程と節目をたたむ）。
 *     期間の外にある工程は右端に「10/13 ›」（左なら「‹ 9/20」）。押すとそこへ
 *   行を押すと開く（開くのは1つだけ）：開始・終わり・日数・進み（遅れは朱）・「n日遅れ」・担当・「進みを入れる」（人数の字は #297 でやめた）
 *   E3 進みを入れる：下から出るシート。0%・25%・50%・75%・済み と −5・＋5。押したらすぐ保存（トースト＋元に戻す）。見るだけでも入れられる
 *   E4 直す：開いた行の欄が直せる（開始＝日付の欄・日数＝−／＋・担当・人数＝−／＋（#297 から人の列を ON にした人だけ。OFF は ⋮「人数」）・⋮）、バーは長押しで動かし、選んだバーの両端のつまみで日数。
 *     下に「＋ 工程を追加」。見出し・メモ・節目は工程表の ⋮ から
 *   はじめての案内は1回だけ（× で消す。その人のブラウザに覚える）
 * 計算は packages/contract（normalizeSchedule・dragPatch は PC と同じ）。保存・元に戻すは親（Schedule.tsx の ScheduleEditor）
 */
import {
  addDays,
  calendarForRow,
  dayNo,
  isOffDay,
  mdOf,
  mdwLongOf,
  mdwOf,
  normalizeSchedule,
  calendarReleasing,
  visibleRows,
  weekdayOf,
  WEEKDAY_JA,
  ymdOfDayNo,
  type ScheduleAssignees,
  type ScheduleConflict,
  type ScheduleDoc,
  type ScheduleRow,
  type ScheduleDayNote,
  dayNotesByRow,
  mdwJaOf,
  type WorkCalendar,
  TASK_STATE_LABEL,
  TaskState as TaskStateEnum,
  barDonePct,
  taskStateOf,
  type TaskState,
} from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { t } from "../../labels.js";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { backTitle, type useBackNav } from "../../components/BackButton.jsx";
import { CrIcon } from "../../components/Create.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { Help, Switch } from "../../components/ui.jsx";
import { AssigneeInput, assigneeLabel } from "./AssigneeInput.jsx";
import type { GanttOps } from "./Gantt.jsx";
import { usePinchZoom } from "./hooks.js";
import { daysOf, dragPatch, workRuns, xOf } from "./timeline.js";

/** 工程の列の幅（px）・行の高さ（docs/39 §5.3.3） */
export const PHONE_NAME_W = 128;
const PHONE_ROW_H = 56;
/** 週＝1日 20px（拡大・縮小で 12〜48px）、月＝1日 6px */
const PHONE_PX = { week: 20, month: 6 } as const;
const LONG_PRESS_MS = 400;

/* ---------------- 「見るだけ」⇔「直す」のスイッチ（スマホ・タブレット） ---------------- */
export function EditSwitch({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={"sch-editsw" + (on ? " on" : "")} data-testid="sch-edit-switch">
      {on ? null : (
        <svg className="cri" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      )}
      <span className="t">{on ? SCHEDULE_MSG.edit : SCHEDULE_MSG.viewOnly}</span>
      <input type="checkbox" role="switch" aria-checked={on} aria-label={SCHEDULE_MSG.editSwitch} checked={on} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" aria-hidden="true">
        <span className="thumb" />
      </span>
    </label>
  );
}

type Status = { k: "idle" | "saving" | "saved" | "error"; msg?: string };

export function PhoneSchedule({
  title,
  back,
  backLabel,
  doc,
  cal,
  today,
  edit,
  onEdit,
  ops,
  cands,
  delays,
  conflicts,
  progress,
  pct = true,
  crew = false,
  done: doneN = { done: 0, total: 0 },
  onState,
  lastMs,
  end,
  status,
  moreItems,
  exportButton,
  onAddTask,
  onProgress,
  onConflict,
  dayPx,
  onZoom,
  notice,
  dayNotes = [],
}: {
  title: string;
  back: ReturnType<typeof useBackNav>;
  backLabel: string;
  doc: ScheduleDoc;
  cal: WorkCalendar;
  today: string;
  edit: boolean;
  onEdit: (v: boolean) => void;
  ops: GanttOps;
  cands: ScheduleAssignees | undefined;
  delays: ReadonlyMap<string, number>;
  conflicts: ReadonlyMap<string, ScheduleConflict>;
  progress: number | null;
  /** 進捗を％で入力（#292。false なら状況＝未着手／着手／完了の3つのボタン・帯は「完了 n／m{task}」） */
  pct?: boolean;
  /** 人の列（#297。帯の ⋮「表示設定」の「人」・既定は出さない）：ON のときだけ 開いた行に人数の −／＋。OFF は{task}の ⋮「人数」 */
  crew?: boolean;
  done?: { done: number; total: number };
  /** 状況を変える（#292。すぐ保存・トーストと元に戻すは親） */
  onState?: (rowId: string, to: TaskState) => void;
  lastMs: { name: string; date: string } | null;
  end: string | null;
  status: Status;
  /** 工程表の ⋮（見出し・メモ・節目の追加ほか） */
  moreItems: RowMenuItem[];
  /** 「出力」のボタン（#298、docs/102 §2.4.2。閲覧のみ・編集とも上の段。前は見るだけのときの ⋮） */
  exportButton?: ReactNode;
  onAddTask: () => void;
  /** 進みを入れる（すぐ保存・トーストと元に戻すは親） */
  onProgress: (rowId: string, n: number) => void;
  onConflict: (rowId: string, anchor: HTMLElement) => void;
  dayPx: number | null;
  onZoom: (px: number | null) => void;
  /** 協力会社からの報告の知らせの行（#103）。期間の下・表の上に出す */
  notice?: ReactNode;
  /** 日のメモ（#299）：行の下辺の朱の点線・開いた行に一覧・日付の見出しの点（直すときは見出しの長押しで吹き出し） */
  dayNotes?: readonly ScheduleDayNote[];
}) {
  const [mode, setMode] = useState<"week" | "month">("week");
  const notesOf = useMemo(() => dayNotesByRow(dayNotes), [dayNotes]);
  const headNotes = notesOf.get("");
  // 日付の見出しの長押し（#299。直すとき）：0.4秒で日付の見出しのメモの吹き出し
  const headPress = useRef<number | null>(null);
  const headDown = (e: RPointerEvent<HTMLElement>, d: string) => {
    if (!edit || !ops.noteEdit) return;
    const at = { x: e.clientX, y: e.clientY };
    if (headPress.current) window.clearTimeout(headPress.current);
    headPress.current = window.setTimeout(() => {
      headPress.current = null;
      ops.noteEdit?.({ rowId: null, date: d, at });
    }, LONG_PRESS_MS);
  };
  const headUp = () => {
    if (headPress.current) window.clearTimeout(headPress.current);
    headPress.current = null;
  };
  const px = mode === "week" ? (dayPx ?? PHONE_PX.week) : PHONE_PX.month;
  const [open, setOpen] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [sheet, setSheet] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ left: 0, width: 300 });
  // 2本指で拡大・縮小（週の表示に切り替えて 1日 12〜48px）
  const zoom = useCallback(
    (n: number) => {
      setMode("week");
      onZoom(n);
    },
    [onZoom],
  );
  usePinchZoom(scroller, px, zoom, PHONE_NAME_W);

  // 描く範囲：日付のある行と今日を含み、前に1週・後ろに4週（PC と同じ考え方）
  const range = useMemo(() => {
    let lo = today;
    let hi = today;
    for (const r of doc.rows) for (const d of [r.start, r.end]) if (d) (d < lo && (lo = d), d > hi && (hi = d));
    const start = addDays(lo, -7 - ((weekdayOf(addDays(lo, -7)) + 6) % 7));
    const endDay = addDays(hi, 28);
    return { start, days: dayNo(endDay) - dayNo(start) + 1 };
  }, [doc.rows, today]);
  const x = (ymd: string) => xOf(ymd, range.start, px);
  const days = useMemo(() => daysOf(range.start, range.days), [range.start, range.days]);

  // はじめ：今日の2日前から。1日の幅が変わっても見えている最初の日を動かさない
  const firstDay = useRef<string | null>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const d = firstDay.current ?? addDays(today, -2);
    el.scrollLeft = Math.max(0, (dayNo(d) - dayNo(range.start)) * px);
    setScroll({ left: el.scrollLeft, width: el.clientWidth - PHONE_NAME_W });
  }, [px, range.start]); // eslint-disable-line react-hooks/exhaustive-deps
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    firstDay.current = ymdOfDayNo(dayNo(range.start) + Math.floor(el.scrollLeft / px));
    setScroll({ left: el.scrollLeft, width: el.clientWidth - PHONE_NAME_W });
  };
  const leftDay = ymdOfDayNo(dayNo(range.start) + Math.floor(scroll.left / px));
  const rightDay = ymdOfDayNo(dayNo(range.start) + Math.floor((scroll.left + Math.max(px, scroll.width)) / px) - 1);
  const step = (n: number) => scroller.current?.scrollBy({ left: n * px, behavior: "smooth" });
  const goTo = (ymd: string) => {
    const el = scroller.current;
    if (el) el.scrollLeft = Math.max(0, x(ymd) - 2 * px);
  };

  // 行：済んだ工程と節目は「済み n工程」にたたむ。見出しとメモは区切りの行（直すときは名前・文を打てる）
  const vis = visibleRows(doc.rows);
  const isDone = (r: ScheduleRow) => (r.kind === "task" || r.kind === "milestone") && (r.progress ?? 0) >= 100;
  const done = vis.filter(isDone);
  const rest = vis.filter((r) => !isDone(r));
  const indexOf = useMemo(() => new Map(doc.rows.map((r, i) => [r.id, i])), [doc.rows]);

  /* ---------------- 直す：バーのドラッグ（指は長押しでつかむ・マウスはすぐ） ---------------- */
  const [preview, setPreview] = useState<ScheduleDoc | null>(null);
  const drag = useRef<{ id: string; t: "move" | "resl" | "resr"; x0: number; moved: boolean; next: ScheduleDoc | null } | null>(null);
  const stopTouch = (e: TouchEvent) => e.cancelable && e.preventDefault();
  const endDrag = (commit: boolean) => {
    const d = drag.current;
    drag.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onCancel);
    window.removeEventListener("touchmove", stopTouch);
    document.body.classList.remove("sch-dragging");
    setPreview(null);
    if (commit && d?.moved && d.next) ops.replace(d.next, { save: "now" });
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dd = Math.round((e.clientX - d.x0) / px);
    const r = doc.rows.find((z) => z.id === d.id);
    if (!r) return;
    const patch = dragPatch(r, d.t, dd, cal);
    if (!patch) return;
    if (dd !== 0) d.moved = true;
    d.next = normalizeSchedule({ rows: doc.rows.map((z) => (z.id === d.id ? { ...z, ...patch } : z)), links: doc.links }, calendarReleasing(cal, r, patch.start));
    setPreview(d.next);
  };
  const onUp = () => endDrag(true);
  const onCancel = () => endDrag(false);
  const begin = (id: string, t: "move" | "resl" | "resr", x0: number) => {
    drag.current = { id, t, x0, moved: false, next: null };
    document.body.classList.add("sch-dragging");
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("touchmove", stopTouch, { passive: false });
  };
  const press = useRef<(() => void) | null>(null);
  const onBarDown = (e: RPointerEvent, r: ScheduleRow, t: "move" | "resl" | "resr") => {
    if (!edit || e.button !== 0 || !r.start) return;
    if (e.pointerType === "touch" && t === "move") {
      press.current?.();
      const x0 = e.clientX;
      const y0 = e.clientY;
      let last = x0;
      const pid = e.pointerId;
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener("pointermove", watch);
        window.removeEventListener("pointerup", cleanup);
        window.removeEventListener("pointercancel", cleanup);
        press.current = null;
      };
      const watch = (ev: PointerEvent) => {
        if (ev.pointerId !== pid) return;
        last = ev.clientX;
        if (Math.hypot(ev.clientX - x0, ev.clientY - y0) > 8) cleanup();
      };
      const timer = setTimeout(() => {
        cleanup();
        setOpen(r.id);
        navigator.vibrate?.(10);
        begin(r.id, "move", last);
      }, LONG_PRESS_MS);
      window.addEventListener("pointermove", watch);
      window.addEventListener("pointerup", cleanup);
      window.addEventListener("pointercancel", cleanup);
      press.current = cleanup;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    begin(r.id, t, e.clientX);
  };
  useEffect(
    () => () => {
      press.current?.();
      endDrag(false);
    },
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // 行を追加したら（工程・節目）その行を開く（名前を打てるように）
  const seen = useRef<Set<string>>(new Set(doc.rows.map((r) => r.id)));
  useEffect(() => {
    const added = doc.rows.filter((r) => !seen.current.has(r.id));
    seen.current = new Set(doc.rows.map((r) => r.id));
    const r = added.find((x) => x.kind === "task" || x.kind === "milestone");
    if (added.length === 1 && r) {
      setOpen(r.id);
      requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-row-id="${r.id}"]`)?.scrollIntoView({ block: "nearest" }));
    }
  }, [doc.rows]);

  const shown = preview ?? doc;
  const rowOf = (id: string) => shown.rows.find((r) => r.id === id);
  const lastDate = lastMs?.date ?? end;
  const sheetRow = sheet ? doc.rows.find((r) => r.id === sheet) : undefined;

  const renderRow = (r0: ScheduleRow) => {
    const r = rowOf(r0.id) ?? r0;
    const i = indexOf.get(r.id) ?? -1;
    if (r.kind === "heading" || r.kind === "memo") {
      const hd = r.kind === "heading";
      return (
        <div key={r.id} className={"sch-phrow " + (hd ? "hd" : "memo")} style={{ width: scroll.width + PHONE_NAME_W }} data-testid={hd ? "sch-ph-heading" : "sch-ph-memo"} data-row-id={r.id}>
          {edit ? (
            <>
              <input
                className="sch-in"
                value={r.name}
                maxLength={hd ? 200 : 500}
                placeholder={hd ? "見出し" : SCHEDULE_MSG.memo}
                aria-label={hd ? "見出し" : "メモ"}
                onChange={(e) => ops.update(r.id, { name: e.target.value }, { save: "debounce", key: `name:${r.id}` })}
              />
              {/* ⋮ は表を横に動かしても見える所（カードの右端）に貼り付ける（#103 §6.1 の4） */}
              <span className="sch-phmenu">
                <RowMenu label={`「${r.name || (hd ? "見出し" : "メモ")}」のメニュー`} items={ops.menuItems(i)} testId="sch-menu" />
              </span>
            </>
          ) : hd ? (
            <b>{r.name || "（名前なし）"}</b>
          ) : (
            <span>{r.name}</span>
          )}
        </div>
      );
    }
    const isOpen = open === r.id;
    const late = delays.get(r.id) ?? 0;
    const conflict = conflicts.get(r.id);
    const ms = r.kind === "milestone";
    // 期間の外なら端に日付のしるし
    const visFrom = leftDay;
    const visTo = rightDay;
    const endD = r.end ?? r.start;
    const before = !!r.start && !!endD && endD < visFrom;
    const after = !!r.start && r.start > visTo;
    return (
      <div key={r.id} className={"sch-phgroup" + (isOpen ? " open" : "")}>
        <div
          className={"sch-phrow" + (isOpen ? " open" : "")}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-label={`${r.name || "（名前なし）"}${r.start ? ` ${mdOf(r.start)}${ms ? "" : `〜${mdOf(r.end ?? r.start)}`}` : ""}`}
          onClick={(e) => {
            if ((e.target as Element).closest("[data-no-open]")) return;
            setOpen(isOpen ? null : r.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(isOpen ? null : r.id);
            }
          }}
          data-testid="sch-ph-row"
          data-row-id={r.id}
        >
          <div className="sch-phname">
            {ms ? <span className={"sch-dia sm" + ((r.progress ?? 0) >= 100 ? "" : " hollow")} aria-hidden="true" /> : null}
            <b>{r.name || "（名前なし）"}</b>
          </div>
          <div className="sch-phbars" style={{ width: days.length * px }}>
            {r.start ? <PhoneBar r={r} x={x} px={px} cal={cal} late={late > 0} sel={isOpen && edit} edit={edit} pct={pct} onDown={(e, t) => onBarDown(e, r, t)} /> : null}
            {/* 日のメモ（#299）：行の下辺の朱の点線（文は開いた行の一覧・乗せたときの吹き出し） */}
            {r.kind === "task"
              ? [...(notesOf.get(r.id) ?? [])].map(([d, text]) => (
                  <span key={d} className="sch-dnote" style={{ left: x(d), width: px }} title={`${mdwJaOf(d, cal.holidays)} ${text}`} data-testid="sch-dnote" data-date={d} />
                ))
              : null}
            {before ? (
              <button type="button" className="sch-phedge l" style={{ left: scroll.left + 4 }} data-no-open onClick={() => goTo(r.start!)} data-testid="sch-ph-edge">
                ‹ {mdOf(endD!)}
              </button>
            ) : after ? (
              <button type="button" className="sch-phedge r" style={{ left: scroll.left + scroll.width - 4 }} data-no-open onClick={() => goTo(r.start!)} data-testid="sch-ph-edge">
                {mdOf(r.start!)} ›
              </button>
            ) : null}
          </div>
        </div>
        {isOpen ? (
          <div className="sch-phpanel" style={{ width: scroll.width + PHONE_NAME_W }} data-testid="sch-ph-panel">
            {edit ? (
              <input
                className="inp sch-phnm"
                value={r.name}
                maxLength={200}
                placeholder={ms ? "節目の名前" : t("{task}名")}
                aria-label={ms ? "節目の名前" : t("{task}名")}
                onChange={(e) => ops.update(r.id, { name: e.target.value }, { save: "debounce", key: `name:${r.id}` })}
              />
            ) : null}
            {ms ? (
              <dl className={"sch-phkv" + (edit ? " edit" : "")}>
                <div>
                  <dt>日付</dt>
                  <dd>{edit ? <input className="inp" type="date" value={r.start ?? ""} aria-label="日付" onChange={(e) => e.target.value && ops.update(r.id, { start: e.target.value, end: e.target.value }, { save: "now" })} data-testid="sch-ph-start" /> : r.start ? mdOf(r.start) : "—"}</dd>
                </div>
                <div>
                  <dt>進捗</dt>
                  <dd>{(r.progress ?? 0) >= 100 ? SCHEDULE_MSG.progressDone : "未完了"}</dd>
                </div>
              </dl>
            ) : (
              <dl className={"sch-phkv" + (edit ? " edit" : "")}>
                <div>
                  <dt>開始</dt>
                  <dd>
                    {edit ? (
                      <input
                        className="inp"
                        type="date"
                        value={r.start ?? ""}
                        aria-label="開始"
                        onChange={(e) => e.target.value && ops.update(r.id, { start: e.target.value, days: r.days ?? 1 }, { save: "now" })}
                        data-testid="sch-ph-start"
                      />
                    ) : r.start ? (
                      mdOf(r.start)
                    ) : (
                      "—"
                    )}
                  </dd>
                </div>
                <div>
                  <dt>終わり</dt>
                  <dd data-testid="sch-ph-end">{r.end ? mdOf(r.end) : "—"}</dd>
                </div>
                <div>
                  <dt>日数</dt>
                  <dd>
                    {edit ? (
                      <Stepper
                        value={r.days}
                        min={1}
                        max={999}
                        label="日数"
                        onChange={(n) => ops.update(r.id, n == null ? { days: 1 } : r.start ? { days: n } : { days: n, start: ops.defaultStart(i) }, { save: "now" })}
                        testId="sch-ph-days"
                      />
                    ) : (
                      <span data-testid="sch-ph-days-ro">{r.days != null ? `${r.days}日` : "—"}</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{pct ? "進捗" : SCHEDULE_MSG.stateColumn}</dt>
                  <dd className={late > 0 ? "late" : ""} data-testid="sch-ph-progress">
                    {pct ? ((r.progress ?? 0) >= 100 ? SCHEDULE_MSG.progressDone : `${r.progress ?? 0}%`) : TASK_STATE_LABEL[taskStateOf(r)]}
                  </dd>
                </div>
              </dl>
            )}
            {/* 日のメモ（#299）：その{task}のメモ（日付の順。直すのは ⋮「日ごとのメモ」） */}
            {r.kind === "task" && notesOf.get(r.id)?.size ? (
              <ul className="sch-phnotes" data-testid="sch-ph-notes">
                {[...notesOf.get(r.id)!].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([d, text]) => (
                  <li key={d}>
                    <b>{mdwJaOf(d, cal.holidays)}</b> {text}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="sch-phchips">
              {late > 0 ? <span className="sch-chip shu sm">{SCHEDULE_MSG.delayBadge(late)}</span> : null}
              {edit ? (
                <span className="sch-phasg">
                  <AssigneeInput value={r.assignee} cands={cands} label="担当" onRegister={ops.registerVendor} onChange={(a) => ops.update(r.id, { assignee: a }, { save: "now" })} />
                </span>
              ) : r.assignee ? (
                <span className="sch-phtxt">{assigneeLabel(r.assignee, cands)}</span>
              ) : null}
              {/* 人数（#297）：字は出さない。人の列を ON にした人だけ 直すときに −／＋（OFF は ⋮「人数」） */}
              {r.kind === "task" && edit && crew ? (
                <Stepper value={r.crew} min={1} max={99} label={SCHEDULE_MSG.crewLabel} unit="人" empty onChange={(n) => ops.update(r.id, { crew: n }, { save: "now" })} testId="sch-ph-crew" />
              ) : null}
              {conflict ? (
                <button type="button" className="sch-ovl" onClick={(e) => onConflict(r.id, e.currentTarget)} data-testid="sch-overlap">
                  {SCHEDULE_MSG.overlap}
                </button>
              ) : null}
              {edit ? (
                <span className="sch-phmenu">
                  <RowMenu label={t(`「${r.name || "{task}"}」のメニュー`)} items={ops.menuItems(i)} testId="sch-menu" />
                </span>
              ) : null}
            </div>
            {r.kind === "task" ? (
              <button type="button" className="btn primary sch-phwide" onClick={() => setSheet(r.id)} data-testid="sch-ph-progress-open">
                {SCHEDULE_MSG.progressInput}
              </button>
            ) : (
              // 節目の済み（S-00009 で「済みにする ⇔ まだにする」のボタンから）：スイッチ。ON＝済み。確かめなし・すぐ保存（PC の「済」と同じ）
              <div className="sch-phmssw">
                <Switch
                  checked={(r.progress ?? 0) >= 100}
                  onChange={(v) => onProgress(r.id, v ? 100 : 0)}
                  label={SCHEDULE_MSG.progressDone}
                  testId="sch-ph-msdone"
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="sch-page full ph" data-testid="sch-phone">
      <header className="sch-band ph" data-testid="sch-band">
        <Link className="sch-back" to={back.to} onClick={back.request} title={backTitle(back)} data-testid="create-back">
          <CrIcon k="back" />
          <span className="bkl">
            <span className="bkt">{backLabel}</span>
            <span className="bksr">に戻る</span>
          </span>
        </Link>
        <h1 title={title}>{title}</h1>
        <div className="sch-bandc">
          <span className="crchip" data-testid="sch-ph-progress-chip">
            <CrIcon k="check" />
            {pct ? `進捗 ${progress == null ? "—" : `${progress}%`}` : doneN.total ? SCHEDULE_MSG.doneOf(doneN.done, doneN.total) : "—"}
          </span>
          {lastDate ? (
            <span className="crchip">
              <CrIcon k="cal" />
              {lastMs ? lastMs.name || "最後の節目" : "終わり"} {mdOf(lastDate)}
            </span>
          ) : null}
          {/* 協力会社からの報告のチップ（#171。前は表の上の帯） */}
          {notice}
        </div>
      </header>

      <div className="sch-phops">
        <div className="sch-seg" role="radiogroup" aria-label="表示">
          {(
            [
              ["week", "週"],
              ["month", "月"],
            ] as const
          ).map(([k, t]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={mode === k}
              className={mode === k ? "on" : ""}
              onClick={() => {
                setMode(k);
                if (k === "week") onZoom(null);
              }}
              data-testid={`sch-ph-view-${k}`}
            >
              {t}
            </button>
          ))}
        </div>
        <span className="sp" />
        <EditSwitch on={edit} onChange={onEdit} />
        {/* 使い方（2本指で拡大・縮小／直すときはバーを長押し）は「？」（#171。前は表の上・画面の下の札） */}
        <Help label="使い方" testId="help-sch-phone" text={`${SCHEDULE_MSG.hintPinch}。${SCHEDULE_MSG.hintLongPress}（「編集」がオンのとき）。`} />
        {exportButton}
      </div>
      <div className="sch-phnav">
        <button type="button" className="btn quiet sq" aria-label="前へ" onClick={() => step(mode === "week" ? -7 : -28)}>
          <CrIcon k="back" />
        </button>
        <span className="sch-period" data-testid="sch-period">
          {mdwLongOf(leftDay)}〜 {mdwLongOf(rightDay)}
        </span>
        <button type="button" className="btn quiet sq" aria-label="次へ" onClick={() => step(mode === "week" ? 7 : 28)}>
          <CrIcon k="right" />
        </button>
      </div>
      <span className="hint sch-save" role="status" data-testid="sch-autosave">
        {status.k === "saving" ? SCHEDULE_MSG.saving : status.k === "saved" ? SCHEDULE_MSG.saved : status.k === "error" ? `⚠ ${SCHEDULE_MSG.saveFailed}：${status.msg ?? ""}` : ""}
      </span>

      <div className="paper sch-phcard" data-testid="sch-card">
        <div
          className="sch-phscroll"
          ref={scroller}
          onScroll={onScroll}
          data-testid="sch-scroll"
        >
          <div className="sch-phinner" style={{ width: PHONE_NAME_W + days.length * px }}>
            <div className="sch-phhead">
              <div className="sch-phname">{t("{task}")}</div>
              <div className="sch-phdays" style={{ width: days.length * px }}>
                {px >= 16
                  ? days.map((d, k) => (
                      <span
                        key={d}
                        className={"d" + (isOffDay(d, cal) ? " off" : "") + (cal.siteOff?.has(d) ? " so" : "") + (d === today ? " today" : "")}
                        style={{ left: k * px, width: px }}
                        data-date={d}
                        title={headNotes?.get(d) ? `${mdwJaOf(d, cal.holidays)} ${headNotes.get(d)}` : undefined}
                        onPointerDown={(e) => headDown(e, d)}
                        onPointerUp={headUp}
                        onPointerCancel={headUp}
                        onPointerLeave={headUp}
                        onContextMenu={(e) => edit && e.preventDefault()}
                        data-testid="sch-ph-dayhead"
                      >
                        <b>{Number(d.slice(8))}</b>
                        {/* {siteShort}の休み（#293）は「休」 */}
                        <i>{cal.siteOff?.has(d) ? SCHEDULE_MSG.siteOffMark : cal.holidays.has(d) ? "祝" : WEEKDAY_JA[weekdayOf(d)]}</i>
                        {/* 日付の見出しのメモ（#299）：日付の下に朱の点 */}
                        {headNotes?.has(d) ? <span className="hnote" data-testid="sch-hnote" data-note-date={d} aria-hidden="true" /> : null}
                      </span>
                    ))
                  : days.map((d, k) =>
                      weekdayOf(d) === 1 ? (
                        <span key={d} className="w" style={{ left: k * px }}>
                          {mdOf(d)}
                        </span>
                      ) : null,
                    )}
              </div>
            </div>
            <div className="sch-phbody">
              <div className="sch-phbg" aria-hidden="true" style={{ left: PHONE_NAME_W, width: days.length * px }}>
                {px >= 4 ? days.map((d, k) => (isOffDay(d, cal) ? <span key={d} className="sch-off" style={{ left: k * px, width: px }} /> : null)) : null}
                {dayNo(today) >= dayNo(range.start) ? <span className="sch-today" style={{ left: x(today) + px / 2 - 1 }} /> : null}
              </div>
              {done.length ? (
                <div className="sch-phgroup">
                  <button type="button" className="sch-phrow done" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)} data-testid="sch-ph-done">
                    <span className="sch-phname">
                      <svg className="cri" viewBox="0 0 24 24" aria-hidden="true">
                        <path d={showDone ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"} />
                      </svg>
                      {SCHEDULE_MSG.doneRows(done.length)}
                    </span>
                  </button>
                </div>
              ) : null}
              {showDone ? done.map(renderRow) : null}
              {rest.map(renderRow)}
              {vis.length === 0 ? <p className="hint sch-none">{t("{task}はまだありません。")}</p> : null}
            </div>
          </div>
        </div>
        {edit ? (
          <div className="sch-phadd" data-notice-above="">
            <button type="button" className="btn sch-phwide" onClick={onAddTask} data-testid="sch-add-task">
              <CrIcon k="plus" />
              {SCHEDULE_MSG.addTask}
            </button>
            <RowMenu label={t("{schedule}のメニュー")} items={moreItems} testId="sch-more" />
          </div>
        ) : null}
      </div>


      {sheetRow ? (
        <ProgressSheet
          r={sheetRow}
          who={assigneeLabel(sheetRow.assignee, cands)}
          pct={pct}
          onClose={() => setSheet(null)}
          onSet={(n) => onProgress(sheetRow.id, n)}
          onState={(to) => onState?.(sheetRow.id, to)}
        />
      ) : null}
    </div>
  );
}

/* ---------------- バー（スマホ） ---------------- */
function PhoneBar({
  r,
  x,
  px,
  cal,
  late,
  sel,
  edit,
  onDown,
  pct = true,
}: {
  r: ScheduleRow;
  x: (ymd: string) => number;
  px: number;
  cal: WorkCalendar;
  late: boolean;
  sel: boolean;
  edit: boolean;
  onDown: (e: RPointerEvent, t: "move" | "resl" | "resr") => void;
  pct?: boolean;
}) {
  if (!r.start) return null;
  if (r.kind === "milestone") {
    return (
      <span
        className={"sch-phms" + ((r.progress ?? 0) >= 100 ? " done" : "")}
        style={{ left: x(r.start) + px / 2 - 9 }}
        data-bar-id={r.id}
        data-testid="sch-bar"
        aria-label={`${r.name} ${mdOf(r.start)}`}
        onPointerDown={(e) => onDown(e, "move")}
        onContextMenu={(e) => e.preventDefault()}
      />
    );
  }
  const end = r.end ?? r.start;
  const l = x(r.start);
  const w = x(end) + px - l;
  const runs = workRuns(r.start, end, calendarForRow(r, cal)); // 「休みの日も作業する」工程は休みで切れない
  const total = runs.reduce((n, s) => n + dayNo(s.to) - dayNo(s.from) + 1, 0) || 1;
  // 済んだ所（#292：％ OFF は 完了＝全部・着手＝半分・未着手＝無し）
  let doneLeft = (total * barDonePct(r, pct)) / 100;
  const done = (r.progress ?? 0) >= 100;
  return (
    <div
      className={"sch-bar ph" + (done ? " done" : "") + (late ? " late" : "") + (sel ? " sel" : "") + (edit ? " edit" : "")}
      style={{ left: l, width: w }}
      data-bar-id={r.id}
      data-testid="sch-bar"
      aria-label={`${r.name} ${mdOf(r.start)}〜${mdOf(end)} ${r.days ?? 1}日`}
      onPointerDown={(e) => onDown(e, "move")}
      onContextMenu={(e) => e.preventDefault()}
    >
      {runs.map((s, k) => {
        const n = dayNo(s.to) - dayNo(s.from) + 1;
        const f = Math.max(0, Math.min(n, doneLeft)) / n;
        doneLeft -= n;
        const prev = runs[k - 1];
        return (
          <span key={s.from}>
            {prev ? <span className="gap" style={{ left: x(addDays(prev.to, 1)) - l, width: x(s.from) - x(addDays(prev.to, 1)) }} /> : null}
            <span className="bseg" style={{ left: x(s.from) - l, width: n * px, ["--done" as string]: `${Math.round(f * 100)}%` }} />
          </span>
        );
      })}
      {sel ? (
        <>
          <span className="knob l" data-no-open onPointerDown={(e) => onDown(e, "resl")} aria-hidden="true" data-testid="sch-knob-l" />
          <span className="knob r" data-no-open onPointerDown={(e) => onDown(e, "resr")} aria-hidden="true" data-testid="sch-knob-r" />
        </>
      ) : null}
    </div>
  );
}

/* ---------------- −／＋ と数字（日数・人数） ---------------- */
function Stepper({
  value,
  min,
  max,
  label,
  unit,
  empty,
  onChange,
  testId,
}: {
  value: number | null;
  min: number;
  max: number;
  label: string;
  unit?: string;
  /** 空（人数なし）にできる：min より下げると空 */
  empty?: boolean;
  onChange: (n: number | null) => void;
  testId: string;
}) {
  const down = () => {
    if (value == null) return;
    if (value <= min) return empty ? onChange(null) : undefined;
    onChange(value - 1);
  };
  const up = () => onChange(value == null ? min : Math.min(max, value + 1));
  return (
    <span className="sch-step" role="group" aria-label={label} data-testid={testId}>
      <button type="button" aria-label={`${label}を1つ減らす`} onClick={down} disabled={value == null || (!empty && value <= min)} data-testid={`${testId}-minus`}>
        −
      </button>
      <b data-testid={`${testId}-value`}>{value == null ? "—" : `${value}${unit ?? ""}`}</b>
      <button type="button" aria-label={`${label}を1つ増やす`} onClick={up} disabled={value != null && value >= max} data-testid={`${testId}-plus`}>
        ＋
      </button>
    </span>
  );
}

/* ---------------- E3 進みを入れる（下から出るシート）。#292：％ OFF の会社は 未着手／着手／完了 の3つのボタン ---------------- */
function ProgressSheet({ r, who, pct, onClose, onSet, onState }: { r: ScheduleRow; who: string; pct: boolean; onClose: () => void; onSet: (n: number) => void; onState: (to: TaskState) => void }) {
  const p = r.progress ?? 0;
  const st = taskStateOf(r);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="dim sch-sheetdim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sch-sheet" role="dialog" aria-label={SCHEDULE_MSG.progressInput} data-testid="sch-progress-sheet" data-notice-above="">
        <span className="grab" aria-hidden="true" />
        <div className="hd">
          <b>{SCHEDULE_MSG.progressInput}</b>
          <small>
            {r.name}
            {who ? `・${who}` : ""}
          </small>
        </div>
        {pct ? (
          <>
            <div className="picks">
              {[0, 25, 50, 75, 100].map((n) => (
                <button key={n} type="button" className={"btn" + (p === n ? " primary" : "")} aria-pressed={p === n} onClick={() => onSet(n)} data-testid={`sch-progress-${n}`}>
                  {n >= 100 ? SCHEDULE_MSG.progressDone : `${n}%`}
                </button>
              ))}
            </div>
            <div className="fine">
              <button type="button" className="btn" onClick={() => onSet(Math.max(0, p - 5))} disabled={p <= 0} data-testid="sch-progress-minus">
                −5
              </button>
              <b data-testid="sch-progress-now">{p >= 100 ? SCHEDULE_MSG.progressDone : `${p}%`}</b>
              <button type="button" className="btn" onClick={() => onSet(Math.min(100, p + 5))} disabled={p >= 100} data-testid="sch-progress-plus">
                ＋5
              </button>
            </div>
          </>
        ) : (
          <div className="picks states" role="group" aria-label={SCHEDULE_MSG.stateColumn}>
            {TaskStateEnum.options.map((k) => (
              <button key={k} type="button" className={"btn" + (st === k ? " primary" : "")} aria-pressed={st === k} onClick={() => onState(k)} data-testid={`sch-state-${k}`}>
                {TASK_STATE_LABEL[k]}
              </button>
            ))}
          </div>
        )}
        <p className="hint">{SCHEDULE_MSG.progressHint}</p>
        <button type="button" className="btn sch-phwide" onClick={onClose} data-testid="sch-progress-close">
          {SCHEDULE_MSG.close}
        </button>
      </div>
    </div>
  );
}
